import "flowbite/dist/flowbite.min.css";
import { DropDown } from "../Common/dropDown";
import { InputField } from "../Common/inputField";
import { type ChangeEvent, useEffect } from "react";
import { DatePickerInput } from "../Common/datePicker";
import { useQueryStore } from "../../stores/queryStore";
import { PhoneNumberInputField } from "../Common/phoneNumberInput";
import { formatReferralCode } from "../../utils/formatReferralCode";
import { useAddressLookupStore } from "../../stores/addressLookupStore";
import { formatCanadaPostalCode } from "../../utils/postalCodeFormatter";
import { useUserMortgageProfileStore } from "../../stores/userProfileStore";
import { usePersonalInfoStore } from "../../stores/personalInformationStore";
import { AddressLookupInput } from "../Common/AddressLookup/addressLookpInput";
import {
  CanadianProvinceCodeEnum,
  MaritalStatusMapping,
} from "../../interfaces/mortgageDetailsInterface";

const PersonalInformation = () => {
  const { queryReferralCode } = useQueryStore();

  const {
    firstName,
    middleName,
    lastName,
    address,
    address2,
    city,
    postalCode,
    province,
    phone,
    dateOfBirth,
    maritalStatus,
    isFirstTimeHomeBuyer,
    hasReferralCode,
    referralCode,
    // totalMonthlyDebt,

    setFirstName,
    setMiddleName,
    setLastName,
    setAddress,
    setAddress2,
    setCity,
    setPostalCode,
    setProvince,
    setPhone,
    setDateOfBirth,
    setMaritalStatus,
    setIsFirstTimeHomeBuyer,
    setHasReferralCode,
    setReferralCode,
    // setTotalMonthlyDebt,

    firstNameError,
    lastNameError,
    addressError,
    cityError,
    postalCodeError,
    provinceError,
    phoneError,
    dateOfBirthError,
    maritalStatusError,
    isFirstTimeHomeBuyerError,
    hasReferralCodeError,
    referralCodeError,
    // totalMonthlyDebtError,
  } = usePersonalInfoStore();
  const { userMortgageProfile } = useUserMortgageProfileStore();

  useEffect(() => {
    setFirstName(userMortgageProfile?.firstName || "");
    setMiddleName(userMortgageProfile?.middleName || "");
    setLastName(userMortgageProfile?.lastName || "");
    setAddress(userMortgageProfile?.address?.address1 || "");
    setAddress2(userMortgageProfile?.address?.address2 || "");
    setCity(userMortgageProfile?.address?.city || "");
    setPostalCode(userMortgageProfile?.address?.postalCode || "");
    setProvince(userMortgageProfile?.address?.state || "");
    setPhone(userMortgageProfile?.phoneNumber || "");
    setDateOfBirth(userMortgageProfile?.dateOfBirth || null);
    setMaritalStatus(userMortgageProfile?.maritalStatus || "");
    setIsFirstTimeHomeBuyer(
      userMortgageProfile?.isFirstTimeHomeBuyer === undefined
        ? null
        : userMortgageProfile?.isFirstTimeHomeBuyer
    );
    setHasReferralCode(!!(userMortgageProfile?.referralCode || queryReferralCode));
    setReferralCode(
      formatReferralCode((userMortgageProfile?.referralCode || queryReferralCode) ?? "")
    );
    // setTotalMonthlyDebt(
    //   userMortgageProfile?.totalMonthlyDebt
    //     ? convertToFormattedAmount(
    //         userMortgageProfile.totalMonthlyDebt.amount
    //       ).toString()
    //     : ""
    // );
  }, [
    userMortgageProfile,
    setFirstName,
    setMiddleName,
    setLastName,
    setAddress,
    setAddress2,
    setCity,
    setPostalCode,
    setProvince,
    setPhone,
    setDateOfBirth,
    setMaritalStatus,
    setIsFirstTimeHomeBuyer,
    setHasReferralCode,
    queryReferralCode,
    setReferralCode,
    // setTotalMonthlyDebt,
  ]);

  const { formattedAddresses } = useAddressLookupStore();

  const maritalStatusOptions = Object.keys(MaritalStatusMapping) as Array<
    keyof typeof MaritalStatusMapping
  >;
  const provinceOptions = Object.values(CanadianProvinceCodeEnum);

  useEffect(() => {
    const formattedAddress = formattedAddresses.personalInformation;
    if (formattedAddress) {
      setAddress(formattedAddress.address1);
      setAddress2(formattedAddress.address2 || "");
      setCity(formattedAddress.city);
      setPostalCode(formattedAddress.postalCode);
      setProvince(formattedAddress.state);
    }
  }, [
    formattedAddresses.personalInformation,
    setAddress,
    setAddress2,
    setCity,
    setPostalCode,
    setProvince,
  ]);

  const handlePhoneChange = (rawValue: string) => {
    setPhone(rawValue);
  };

  const handleReferralCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setReferralCode(formatReferralCode(e.target.value));
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="font-bold text-2xl">Tell us about yourself</div>
        <div className="text-[#6B7280]">
          We'll need a few details to get started with your application. This
          information helps us tailor your mortgage options to your needs. Rest
          assured, your data is secure and used only for your application.
        </div>
      </div>
      <hr className="bg-[#6B7280]" />

      <div className="flex flex-col lg:flex-row w-full justify-between gap-4">
        <div className="w-full space-y-1">
          <InputField
            id="firstName"
            label="Legal First Name*"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          {firstNameError && (
            <p className="text-red-600 text-sm">{firstNameError}</p>
          )}
        </div>
        <div className="w-full">
          <InputField
            id="middleName"
            label="Middle Name"
            autoComplete="additional-name"
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
          />
        </div>
        <div className="w-full space-y-1">
          <InputField
            id="lastName"
            label="Legal Last Name*"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          {lastNameError && (
            <p className="text-red-600 text-sm">{lastNameError}</p>
          )}
        </div>
      </div>

      <AddressLookupInput type={"personalInformation"} />

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="w-full space-y-1">
          <InputField
            id="address"
            label="Address*"
            autoComplete="address-line1"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          {addressError && (
            <p className="text-red-600 text-sm">{addressError}</p>
          )}
        </div>
        <div className="w-full">
          <InputField
            id="address2"
            label="Address Line 2"
            autoComplete="address-line2"
            value={address2}
            onChange={(e) => setAddress2(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="w-full space-y-1">
          <InputField
            id="city"
            label="City*"
            autoComplete="address-level2"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          {cityError && <p className="text-red-600 text-sm">{cityError}</p>}
        </div>
        <div className="w-full grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <InputField
              id="postalCode"
              label="Postal Code*"
              autoComplete="postal-code"
              value={formatCanadaPostalCode(postalCode)}
              onChange={(e) => setPostalCode(e.target.value)}
            />
            {postalCodeError && (
              <p className="text-red-600 text-sm">{postalCodeError}</p>
            )}
          </div>
          <div className="space-y-1">
            <DropDown
              label="Province*"
              options={provinceOptions}
              selectedOption={province}
              onSelect={(value) => setProvince(value)}
            />
            {provinceError && (
              <p className="text-red-600 text-sm">{provinceError}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row w-full justify-between gap-4">
        <div className="w-full flex flex-col gap-1 space-y-1">
          <PhoneNumberInputField
            id="phone"
            label="Phone number*"
            value={phone}
            onChange={handlePhoneChange}
          />
          {phoneError && <p className="text-red-600 text-sm">{phoneError}</p>}
        </div>

        <div className="w-full space-y-1">
          <DatePickerInput
            label="Date of Birth*"
            selectedDate={dateOfBirth}
            disableFuture={true}
            onDateChange={setDateOfBirth}
          />
          {dateOfBirthError && (
            <p className="text-red-600 text-sm">{dateOfBirthError}</p>
          )}
        </div>
      </div>

      <div className="w-full flex flex-col lg:flex-row gap-4">
        <div className="space-y-1 w-full">
          <DropDown
            label="Marital Status*"
            options={maritalStatusOptions}
            selectedOption={
              maritalStatusOptions.find(
                (key) => MaritalStatusMapping[key] === maritalStatus
              ) || ""
            }
            onSelect={(value) =>
              setMaritalStatus(
                MaritalStatusMapping[value as keyof typeof MaritalStatusMapping]
              )
            }
          />
          {maritalStatusError && (
            <p className="text-red-600 text-sm">{maritalStatusError}</p>
          )}
        </div>
        <div className="space-y-1 w-full">
          <DropDown
            label="Are you a first-time home buyer?*"
            options={["Yes", "No"]}
            selectedOption={
              isFirstTimeHomeBuyer === null
                ? ""
                : isFirstTimeHomeBuyer
                  ? "Yes"
                  : "No"
            }
            onSelect={(value) => {
              setIsFirstTimeHomeBuyer(
                value === "Yes" ? true : value === "No" && false
              );
            }}
          />
          {isFirstTimeHomeBuyerError && (
            <p className="text-red-600 text-sm">{isFirstTimeHomeBuyerError}</p>
          )}
        </div>
      </div>

      <div className="w-full flex flex-col lg:flex-row gap-4">
        <div className="space-y-1 w-full">
          <DropDown
            label="Do you have a referral code?*"
            options={["Yes", "No"]}
            selectedOption={
              hasReferralCode === null
                ? ""
                : hasReferralCode
                  ? "Yes"
                  : "No"
            }
            onSelect={(value) => {
              setReferralCode("");
              setHasReferralCode(
                value === "Yes" ? true : value === "No" && false
              );
            }
            }
          />
          {hasReferralCodeError && <p className="text-red-600 text-sm">{hasReferralCodeError}</p>}

        </div>
        <div className="space-y-1 w-full">
          {hasReferralCode && (
            <>
              <InputField
                id="referralCode"
                type="text"
                label="Enter Referral Code*"
                value={referralCode}
                onChange={handleReferralCodeChange}
                inputMode="numeric"
                maxLength={15}
              />
              {referralCodeError && <p className="text-red-600 text-sm">{referralCodeError}</p>}
            </>
          )}
        </div>
      </div>
      {/* <div className="w-full flex flex-col lg:flex-row gap-4">
        <div className="space-y-1 w-full lg:w-1/2">
          <CurrencyInputField
            id="totalMonthlyDebt"
            label="Total Monthly Debt*"
            value={totalMonthlyDebt}
            onChange={setTotalMonthlyDebt}
          />
          {totalMonthlyDebtError && (
            <p className="text-red-600 text-sm">{totalMonthlyDebtError}</p>
          )}
        </div>
      </div> */}
    </div>
  );
};

export { PersonalInformation };
